import type { ReactNode } from 'react';
import { Avatar, Icon, IconButton } from '../design';
import { BRAND } from './format';

export type NavSection = 'chat' | 'announce' | 'portal' | 'task' | 'storage' | 'approval' | 'agent';

/** Mục điều hướng chính — Portal/Công việc/Phê duyệt/Agent chỉ hiện "Sắp có" tới W3/W4 (không dữ liệu giả). */
export const NAV_ITEMS: readonly { id: NavSection; icon: string; label: string }[] = [
  { id: 'chat', icon: 'chat', label: 'Chat' },
  { id: 'announce', icon: 'bell', label: 'Thông báo' },
  { id: 'portal', icon: 'grid', label: 'Portal' },
  { id: 'task', icon: 'task', label: 'Công việc' },
  { id: 'storage', icon: 'folder', label: 'Lưu trữ' },
  { id: 'approval', icon: 'file', label: 'Phê duyệt' },
  { id: 'agent', icon: 'bot', label: 'Agent' },
];

/** Thanh điều hướng trái (ẩn trong chế độ nhúng `/embed`). Số trên mục Chat là tổng tin chưa đọc thật. */
export function NavRail({
  active,
  unread,
  announceUnread,
  userName,
  userId,
  dark,
  onSelect,
  onToggleTheme,
  onDevices,
  onBots,
  userPanel,
  userPanelOpen,
  onToggleUserPanel,
}: {
  active: NavSection;
  unread: number;
  /** Số thông báo công ty chưa đọc (huy hiệu trên mục Thông báo). */
  announceUnread: number;
  userName: string;
  userId?: string;
  dark: boolean;
  onSelect: (id: NavSection) => void;
  onToggleTheme: () => void;
  onDevices: () => void;
  onBots: () => void;
  /** Panel người dùng (popover) hiện cạnh ảnh đại diện khi mở. */
  userPanel?: ReactNode;
  userPanelOpen: boolean;
  onToggleUserPanel: () => void;
}) {
  return (
    <nav className="ws-rail" aria-label="Điều hướng chính" data-testid="nav-rail">
      <span className="ws-logo" aria-label={BRAND} role="img">
        <img src="/brand/mark.webp" alt="" width={26} height={30} />
      </span>
      <div className="ws-rail-items">
        {NAV_ITEMS.map((n) => (
          <button
            key={n.id}
            type="button"
            className={active === n.id ? 'is-selected' : undefined}
            aria-current={active === n.id ? 'page' : undefined}
            data-testid={`nav-${n.id}`}
            onClick={() => onSelect(n.id)}
          >
            <span className="ws-rail-icon">
              <Icon name={n.icon} size={22} />
              {n.id === 'chat' && unread > 0 && <b data-testid="nav-unread">{unread > 99 ? '99+' : unread}</b>}
              {n.id === 'announce' && announceUnread > 0 && <b data-testid="nav-announce-unread">{announceUnread > 99 ? '99+' : announceUnread}</b>}
            </span>
            <small>{n.label}</small>
          </button>
        ))}
      </div>
      <div className="ws-rail-bottom">
        <IconButton
          label={dark ? 'Chuyển sang giao diện sáng' : 'Chuyển sang giao diện tối'}
          icon={dark ? 'sun' : 'moon'}
          data-testid="theme-toggle"
          onClick={onToggleTheme}
        />
        <IconButton label="Bot" icon="bot" data-testid="open-bots" onClick={onBots} />
        <IconButton label="Thiết bị" icon="device" data-testid="open-devices" onClick={onDevices} />
        <div className="ws-rail-user-wrap">
          <button
            type="button"
            className="ws-rail-user"
            title={userName}
            aria-label={`Tài khoản: ${userName}`}
            aria-haspopup="dialog"
            aria-expanded={userPanelOpen}
            data-user-panel-trigger
            data-testid="open-user-panel"
            onClick={onToggleUserPanel}
          >
            <Avatar name={userName} seed={userId} size="sm" />
          </button>
          {userPanel}
        </div>
      </div>
    </nav>
  );
}

type ComingSoonSection = Exclude<NavSection, 'chat' | 'announce' | 'storage'>;

const COMING_SOON: Record<ComingSoonSection, { title: string; text: string }> = {
  portal: { title: 'Portal', text: 'Xem dự án, phòng ban và tài liệu của 7wportal ngay trong 7w Chat.' },
  task: { title: 'Công việc', text: 'Giao việc từ hội thoại và theo dõi tiến độ công việc của dự án.' },
  approval: { title: 'Phê duyệt', text: 'Nhận và xử lý các yêu cầu phê duyệt nhiều bước từ 7wportal.' },
  agent: { title: 'Agent', text: 'Trợ lý tự động đọc dữ liệu portal, nhắc việc và đề xuất lịch họp.' },
};

/** Màn "Sắp có" của các mục chưa tích hợp — chỉ mô tả, không số đếm, không dữ liệu. */
export function ComingSoon({ section, onBack }: { section: ComingSoonSection; onBack: () => void }) {
  const info = COMING_SOON[section];
  return (
    <section className="ws-coming-soon" data-testid="coming-soon" data-section={section} aria-labelledby="ws-coming-soon-title">
      <div className="ws-coming-soon-card">
        <span className="ws-coming-soon-icon">
          <Icon name={NAV_ITEMS.find((n) => n.id === section)!.icon} size={28} />
        </span>
        <span className="ws-badge ws-badge-amber">Sắp có</span>
        <h1 id="ws-coming-soon-title">{info.title}</h1>
        <p>{info.text}</p>
        <p className="ws-muted ws-small">Chức năng này sẽ có khi 7w Chat kết nối dữ liệu nghiệp vụ của 7wportal.</p>
        <button type="button" className="ws-btn ws-btn-primary ws-btn-md" onClick={onBack}>
          <Icon name="chat" size={16} />
          Về Chat
        </button>
      </div>
    </section>
  );
}
